// ============================================================================
// Entrada da versão francesa (09/10/2026): e-mail, depois código de 6 dígitos.
//
// A VSL francesa promete "pas de mot de passe à retenir", então aqui não existe
// senha. Só esta tela muda; o americano segue no /auth com senha.
//
// Design: direção A da design-dupla com o Astra (design-2026-10-09-auth-fr/),
// escolhida pelo dono ("vamos de A... versao de desktop... otimizado 100%"). A
// porta tem a cara do que ela comprou: creme, azul-marinho, o traço amarelo e a
// mãe agachada com a filha, a cena da VSL e da capa do checkout. Desktop: marca
// e foto à esquerda, formulário à direita. Escuro: tokens do Astra, com o botão
// creme (o azul-marinho sumia no fundo escuro: 1,05:1).
//
// Quem não comprou é recusado pelo SERVIDOR (gancho before-user-created,
// scripts/fr/sql/001 e 003), não por esta tela. /welcome-hp cadastra sem
// conferir compra (decisão do dono), /auth só compradora.
//
// Régua da avó (CLAUDE.md): nada abaixo de 15 px, um botão principal por passo,
// ajuda por extenso, nenhum controle depende de animação para aparecer.
// ============================================================================
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

const SUPPORT_EMAIL = 'support@codeofobedience.com';

const normalizeEmail = (email: string): string => email.trim().toLowerCase();
const isEmailValid = (email: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

type Step = 'email' | 'code';

function messageForSendError(raw: string | undefined): string {
  const m = (raw || '').toLowerCase();
  if (m.includes('aucun achat')) {
    return "Nous n'avons trouvé aucun achat avec cette adresse. Utilisez l'adresse e-mail donnée au moment du paiement. Si vous venez de payer, attendez une minute et réessayez.";
  }
  if (m.includes('rate limit') || m.includes('too many') || m.includes('security purposes')) {
    return "Vous avez demandé plusieurs codes à la suite. Attendez une minute, puis réessayez.";
  }
  return "Le code n'a pas pu être envoyé. Vérifiez votre connexion, puis réessayez.";
}

function messageForVerifyError(raw: string | undefined): string {
  const m = (raw || '').toLowerCase();
  if (m.includes('expired')) return 'Ce code a expiré. Touchez « Renvoyer un code » pour en recevoir un nouveau.';
  return "Ce code ne correspond pas. Vérifiez les 6 chiffres du dernier e-mail reçu.";
}

// Tokens da direção A (claro / escuro). Cor só por aqui: nada de hex solto no JSX abaixo.
const T = {
  page: 'bg-[#F7F1E7] text-[#101E30] dark:bg-[#111923] dark:text-[#F7F1E7]',
  body: 'text-[#344256] dark:text-[#C3C9D1]',
  strong: 'text-[#101E30] dark:text-[#F7F1E7]',
  field:
    'bg-white border-[1.5px] border-[#8C8982] text-[#101E30] placeholder:text-[#67635D] ' +
    'focus:border-[#163963] focus:shadow-[0_0_0_3px_rgba(22,57,99,0.22)] ' +
    'dark:bg-[#1B2634] dark:border-[#8795A8] dark:text-[#F7F1E7] dark:placeholder:text-[#C3C9D1] ' +
    'dark:focus:border-[#EBC550] dark:focus:shadow-[0_0_0_3px_rgba(235,197,80,0.35)]',
  button:
    'bg-[#101E30] text-white hover:bg-[#1B2C44] active:bg-[#0B1522] ' +
    'dark:bg-[#F7F1E7] dark:text-[#101E30] dark:hover:bg-white',
  link: 'text-[#101E30] underline underline-offset-4 decoration-[1.5px] dark:text-[#F7F1E7]',
  rule: 'border-[#D5CEC3] dark:border-[#3A4656]',
  error: 'text-[#B42318] dark:text-[#FDA29B]',
  ok: 'text-[#1F6B3A] dark:text-[#86EFAC]',
};

// "e-mail" não pode quebrar no hífen ("e-" numa linha e "mail" na outra, visto no render a 375 px).
const EMail = () => <span className="whitespace-nowrap">e-mail</span>;

function Marque() {
  return (
    <div>
      <p className="text-[24px] font-bold leading-[28px] tracking-[-0.01em] lg:text-[44px] lg:leading-[48px]">
        La Méthode
        <br />
        des 3 Phrases
      </p>
      <span aria-hidden className="mt-3 block h-1 w-[70px] rounded-full bg-[#EBC550] lg:mt-4 lg:h-2 lg:w-[120px]" />
    </div>
  );
}

export default function AuthCodeFr() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('email');
  // Como o /welcome-hp americano: o link do e-mail de compra traz o endereço (?e=), e ela não digita nada.
  const [email, setEmail] = useState(() => {
    const sp = new URLSearchParams(window.location.search);
    const candidate = (sp.get('e') || sp.get('email') || sp.get('buyer_email') || sp.get('off_email') || '').trim().toLowerCase();
    return isEmailValid(candidate) ? candidate : '';
  });
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Código vencido: a ação principal vira "Renvoyer un code" (achado 1 do Astra: "Entrer" conduzia ao que acabou de falhar).
  const [expired, setExpired] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const codeRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (user) navigate('/', { replace: true });
  }, [user, navigate]);

  useEffect(() => {
    if (step === 'code') codeRef.current?.focus();
  }, [step]);

  async function requestCode(target: string): Promise<boolean> {
    const urlParams = new URLSearchParams(window.location.search);
    const { error: err } = await supabase.auth.signInWithOtp({
      email: target,
      options: {
        shouldCreateUser: true,
        data: {
          email: target,
          signup_source: window.location.pathname,
          signup_referrer: document.referrer || null,
          signup_utm_source: urlParams.get('utm_source') || null,
          signup_user_agent: navigator.userAgent.slice(0, 500),
        },
      },
    });
    if (err) {
      setError(messageForSendError(err.message));
      return false;
    }
    return true;
  }

  async function sendCode(e?: React.FormEvent) {
    e?.preventDefault();
    const clean = normalizeEmail(email);
    if (!clean || !isEmailValid(clean)) {
      setError("Cette adresse e-mail ne semble pas complète. Vérifiez-la.");
      return;
    }
    setLoading(true);
    setError(null);
    setNotice(null);
    const ok = await requestCode(clean);
    setLoading(false);
    if (!ok) return;
    setEmail(clean);
    setCode('');
    setStep('code');
  }

  async function resendCode() {
    setLoading(true);
    setError(null);
    setNotice(null);
    const ok = await requestCode(email);
    setLoading(false);
    if (ok) {
      setCode('');
      setExpired(false);
      setNotice('Un nouveau code a été envoyé. Utilisez celui du dernier e-mail.');
      codeRef.current?.focus();
    }
  }

  function changeEmail() {
    setStep('email');
    setExpired(false);
    setError(null);
    setNotice(null);
    setCode('');
    window.setTimeout(() => emailRef.current?.focus(), 0);
  }

  async function verify(e?: React.FormEvent) {
    e?.preventDefault();
    const token = code.replace(/\D/g, '');
    if (token.length !== 6) {
      setError('Le code a 6 chiffres.');
      return;
    }
    setLoading(true);
    setError(null);
    setNotice(null);
    const { data, error: err } = await supabase.auth.verifyOtp({ email, token, type: 'email' });
    setLoading(false);
    if (err) {
      setError(messageForVerifyError(err.message));
      if ((err.message || '').toLowerCase().includes('expired')) setExpired(true);
      return;
    }
    // Mesmo cuidado do americano: o PWA do iPhone perde a sessão sem isto.
    if (data?.session) {
      localStorage.setItem('saved_pwa_session', JSON.stringify({
        access_token: data.session.access_token,
        refresh_token: data.session.refresh_token,
      }));
    }
    navigate('/', { replace: true });
  }

  // No desktop o formulário cresce como na referência (campo e botão de 64, letra de 20).
  // focus-visible: a regra global do index.css (*:focus-visible) punha um segundo aro e cantos de 4 px no campo
  // focado (reinspeção do Astra). Aqui o foco é só a sombra própria, com os cantos de 12 px.
  const fieldClass = `h-[58px] w-full rounded-[12px] px-4 text-[18px] outline-none focus-visible:outline-none focus-visible:rounded-[12px] transition-[border-color,box-shadow] lg:h-[68px] lg:px-5 lg:text-[22px] ${T.field}`;
  const buttonClass = `h-[58px] w-full rounded-[12px] text-[19px] font-bold transition-colors disabled:opacity-60 lg:h-[68px] lg:text-[22px] ${T.button}`;
  const labelClass = `mb-2 block text-[17px] font-semibold leading-6 lg:text-[20px] lg:leading-7 ${T.strong}`;
  const bodyClass = `mt-4 text-[18px] leading-[26px] lg:text-[22px] lg:leading-[32px] ${T.body}`;
  const titleClass = 'text-[32px] font-bold leading-[36px] tracking-[-0.01em] lg:text-[48px] lg:leading-[52px] xl:text-[56px] xl:leading-[60px]';

  return (
    <div className={`min-h-[100dvh] w-full overflow-y-auto ${T.page}`}>
      <div
        className="mx-auto w-full max-w-[460px] px-6 lg:grid lg:min-h-[100dvh] lg:max-w-[1232px] lg:grid-cols-[minmax(0,567fr)_minmax(0,604fr)] lg:items-center lg:gap-[62px] lg:px-10"
        style={{ paddingTop: 'max(2rem, env(safe-area-inset-top))', paddingBottom: 'max(2rem, env(safe-area-inset-bottom))' }}
      >
        {/* Coluna da marca e da foto (no desktop, à esquerda; no celular, em cima). */}
        <div className="lg:py-10">
          <Marque />
          <div
            className={`mt-6 overflow-hidden rounded-xl ring-1 ring-inset ring-[#101E30]/10 dark:ring-white/10 ${
              step === 'code' ? 'aspect-[327/120]' : 'aspect-[327/160]'
            } lg:mt-8 lg:aspect-auto lg:h-[min(630px,calc(100dvh-19rem))]`}
          >
            <picture>
              <source media="(min-width: 1024px)" srcSet="/fr/entree-portrait.webp" />
              <img
                src="/fr/entree-bande.webp"
                alt=""
                width={960}
                height={470}
                className="h-full w-full object-cover object-[50%_45%] lg:object-[50%_50%]"
                decoding="async"
              />
            </picture>
          </div>
        </div>

        {/* Coluna do formulário. */}
        <main className="pb-4 lg:py-10">
          {step === 'email' ? (
            <form onSubmit={sendCode} noValidate className="mt-6 lg:mt-0">
              <h1 className={titleClass}>
                Entrez avec
                <br />
                votre <EMail />
              </h1>
              <p className={bodyClass}>
                Pas de mot de passe. Recevez votre code à 6&nbsp;chiffres par <EMail />.
              </p>
              <label htmlFor="fr-email" className={`mt-6 lg:mt-8 ${labelClass}`}>
                L’adresse <EMail /> de votre achat
              </label>
              <input
                id="fr-email"
                ref={emailRef}
                type="email"
                inputMode="email"
                autoComplete="email"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                enterKeyHint="send"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setError(null); }}
                placeholder="vous@exemple.fr"
                className={fieldClass}
                aria-invalid={error ? true : undefined}
                aria-describedby={error ? 'fr-auth-error' : undefined}
              />
              {error && <p id="fr-auth-error" role="alert" className={`mt-3 text-[17px] leading-6 lg:text-[20px] lg:leading-[28px] ${T.error}`}>{error}</p>}
              <button type="submit" disabled={loading} className={`mt-5 ${buttonClass}`}>
                {loading ? 'Envoi en cours…' : 'Recevoir mon code'}
              </button>
            </form>
          ) : (
            <form onSubmit={verify} noValidate className="mt-6 lg:mt-0">
              <h1 className={titleClass}>
                Tapez le code reçu
              </h1>
              {/* Destinatário em linha própria (achado 2 do Astra): ela confere de relance para onde foi. */}
              <p className={bodyClass}>Un code a été envoyé à</p>
              <p className={`mt-1 text-[18px] font-bold leading-[26px] [overflow-wrap:anywhere] lg:text-[22px] lg:leading-[32px] ${T.strong}`}>{email}</p>
              <p className={`mt-2 text-[18px] leading-[26px] lg:text-[22px] lg:leading-[32px] ${T.body}`}>
                Il arrive en général en moins d’une minute. Pensez à regarder dans les spams.
              </p>
              <label htmlFor="fr-code" className={`mt-6 lg:mt-8 ${labelClass}`}>
                Le code à 6&nbsp;chiffres
              </label>
              <input
                id="fr-code"
                ref={codeRef}
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]*"
                maxLength={6}
                enterKeyHint="go"
                value={code}
                onChange={(e) => { setCode(e.target.value.replace(/\D/g, '').slice(0, 6)); setError(null); }}
                className={`${fieldClass} text-center font-semibold tracking-[0.5em] text-[26px]`}
                aria-invalid={error ? true : undefined}
                aria-describedby={error ? 'fr-auth-error' : notice ? 'fr-auth-notice' : undefined}
              />
              {error && <p id="fr-auth-error" role="alert" className={`mt-3 text-[17px] leading-6 lg:text-[20px] lg:leading-[28px] ${T.error}`}>{error}</p>}
              {notice && !error && <p id="fr-auth-notice" role="status" className={`mt-3 text-[17px] leading-6 lg:text-[20px] lg:leading-[28px] ${T.ok}`}>{notice}</p>}
              {expired ? (
                <button type="button" onClick={resendCode} disabled={loading} className={`mt-5 ${buttonClass}`}>
                  {loading ? 'Envoi en cours…' : 'Renvoyer un code'}
                </button>
              ) : (
                <button type="submit" disabled={loading} className={`mt-5 ${buttonClass}`}>
                  {loading ? 'Vérification…' : 'Entrer'}
                </button>
              )}
              <div className="mt-3 flex flex-col items-start">
                <button type="button" onClick={changeEmail} disabled={loading} className={`min-h-12 text-[18px] lg:text-[20px] ${T.link}`}>
                  Changer d’adresse <EMail />
                </button>
                {!expired && (
                  <button type="button" onClick={resendCode} disabled={loading} className={`min-h-12 text-[18px] lg:text-[20px] ${T.link}`}>
                    Renvoyer un code
                  </button>
                )}
              </div>
            </form>
          )}

          <div className={`mt-8 border-t pt-6 lg:mt-10 ${T.rule}`}>
            <p className={`text-[18px] font-semibold leading-[26px] lg:text-[22px] lg:leading-[30px] ${T.strong}`}>Besoin d’aide&nbsp;?</p>
            <p className={`mt-1 text-[17px] leading-6 lg:text-[20px] lg:leading-[30px] ${T.body}`}>Écrivez-nous à</p>
            <a href={`mailto:${SUPPORT_EMAIL}`} className={`inline-block py-1 text-[17px] font-medium lg:text-[20px] ${T.link}`}>{SUPPORT_EMAIL}</a>
            <p className={`text-[17px] leading-6 lg:text-[20px] lg:leading-[30px] ${T.body}`}>Nous répondons en français.</p>
          </div>
        </main>
      </div>
    </div>
  );
}
